import { DeviceStatusInfo, ExceptionEvent } from "@fairfleet/geotab";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { fetchExceptionEvents, FetchExceptionEventsResult, GeotabCall } from "./fetchExceptionEvents";
import { locateVisits } from "./locateVisits";
import { stayQueryPlan, StayQueryPlan } from "./stayQueryPlan";
import { useGeotabClient } from "@/components/GeotabClientContext";
import { MAX_EXCEPTION_EVENT_PAGES, RESULTS_LIMIT_DEVICE, RESULTS_LIMIT_EXCEPTION_EVENT } from "@/constants/resultsLimits";

export interface UseZoneStaysOptions {
  ruleIds: string[];
  /** Only stays of these vehicles are kept. */
  deviceIds: string[];
  from: Date;
  to: Date;
  /** „Zonelogs“: its vehicles' latest data tells a running stay from a finished one. */
  groupId?: string;
  enabled?: boolean;
}

/** What a load cost, shown under the table to compare databases. */
export interface ZoneStaysStats {
  plan: StayQueryPlan["kind"];
  requests: number;
  /** Events Geotab sent, including other rules' events on the vehicle stream. */
  read: number;
  kept: number;
  /** Visits whose start position was read to find their zone. */
  located: number;
  ms: number;
}

const idOf = (ref: unknown) => (typeof ref === "string" ? ref : (ref as { id?: string } | undefined)?.id);

/**
 * The zone visit events in [from, to] for the given vehicles, on the cheaper of two ways (`stayQueryPlan`): one
 * paged read per rule (all at once, bundled by the client), or one paged stream of the vehicles' events filtered to our
 * rules. Then the zones at each visit's start position (`locateVisits`).
 */
export function useZoneStays({ ruleIds, deviceIds, from, to, groupId, enabled = true }: UseZoneStaysOptions) {
  const { geotabClient, activeSessionId } = useGeotabClient();
  const [progress, setProgress] = useState({ done: 0, total: 0 });

  const query = useQuery({
    queryKey: ["zoneStays", activeSessionId, groupId, ruleIds, deviceIds, from.toISOString(), to.toISOString()],
    enabled: enabled && ruleIds.length > 0 && deviceIds.length > 0,
    queryFn: async ({ signal }) => {
      const started = performance.now();
      const call: GeotabCall = (method, params, s) => geotabClient.call(method, params, s);
      const plan = stayQueryPlan({ ruleCount: ruleIds.length, deviceIds, groupId });
      const wantedRules = new Set(ruleIds);
      const wantedDevices = new Set(deviceIds);
      const keep = (event: ExceptionEvent) =>
        wantedRules.has(idOf(event.rule) ?? "") && wantedDevices.has(idOf(event.device) ?? "");
      const paging = { from, to, pageSize: RESULTS_LIMIT_EXCEPTION_EVENT, maxPages: MAX_EXCEPTION_EVENT_PAGES, keep, signal };
      let done = 0;
      const step = (total: number) => () => {
        if (!signal.aborted) setProgress({ done: ++done, total });
      };

      let results: FetchExceptionEventsResult[];
      if (plan.kind === "perRule") {
        setProgress({ done: 0, total: ruleIds.length });
        const onRuleDone = step(ruleIds.length);
        const deviceSearch = plan.deviceId ? { deviceSearch: { id: plan.deviceId } } : {};
        // All at once on purpose: the client collects concurrent Gets for 1.5 s into one ExecuteMultiCall (up to
        // 100 entries) and rate-limits entry by entry. Four at a time waited 1.5 s per round (live: 18 rules 9.5 s).
        results = await Promise.all(
          ruleIds.map(async ruleId => {
            const result = await fetchExceptionEvents(call, { ...paging, search: { ruleSearch: { id: ruleId }, ...deviceSearch } });
            onRuleDone();
            return result;
          }),
        );
      } else {
        // Pages of one stream: their number is only known at the end.
        setProgress({ done: 0, total: 0 });
        results = [await fetchExceptionEvents(call, { ...paging, search: { deviceSearch: plan.deviceSearch }, onPage: step(0) })];
      }

      const events = results.flatMap(r => r.items);
      // Geotab returns every event overlapping the period; only visits that start in it become rows.
      const starting = events.filter(event => {
        const entry = new Date(event.activeFrom);
        return entry >= from && entry <= to;
      });
      // Both at once, so the client bundles them into one call: the latest data is read after the events
      // (a running visit extended meanwhile then still ends at or before it), the start positions need them too.
      const [statuses, located] = await Promise.all([
        groupId
          ? geotabClient.call<DeviceStatusInfo[]>(
              "Get",
              {
                typeName: "DeviceStatusInfo",
                search: { deviceSearch: { groups: [{ id: groupId }] } },
                resultsLimit: RESULTS_LIMIT_DEVICE,
              },
              signal,
            )
          : Promise.resolve([] as DeviceStatusInfo[]),
        locateVisits(call, starting, { signal }),
      ]);
      const lastData = new Map<string, Date>();
      for (const status of statuses) {
        const id = idOf(status.device);
        if (id && status.dateTime) lastData.set(id, new Date(status.dateTime));
      }
      const stats: ZoneStaysStats = {
        plan: plan.kind,
        requests: results.reduce((sum, r) => sum + r.requests, 0) + (groupId ? 1 : 0) + located.requests,
        read: results.reduce((sum, r) => sum + r.read, 0),
        kept: events.length,
        located: located.zones.size,
        ms: Math.round(performance.now() - started),
      };
      return { events, visitZones: located.zones, lastData, stats, complete: results.every(r => r.complete) };
    },
  });

  return {
    events: query.data?.events ?? [],
    visitZones: query.data?.visitZones,
    lastData: query.data?.lastData,
    stats: query.data?.stats,
    complete: query.data?.complete ?? true,
    progress: query.isFetching ? progress : undefined,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
}
